// i18n core: locale is derived from the URL. English lives at the root,
// French under /fr/. A page's canonical (English) path is the key everywhere;
// helpers map it to the right localized URL and back.
import { useLocation } from 'react-router-dom'

export type Locale = 'en' | 'fr'
export const LOCALES: Locale[] = ['en', 'fr']
export const HTML_LANG: Record<Locale, string> = { en: 'en', fr: 'fr-CA' }

// English canonical paths (trailing slash; '/' for home) that have a French version.
// MUST stay in sync with the French routes that actually exist — it drives the
// language switcher, locale-aware links, and hreflang alternates. Grows page by page.
export const FR_ENABLED = new Set<string>([
  '/',
  '/kanban-board/',
  '/calculators/paint/',
  '/calculators/tile/',
  '/calculators/concrete/',
  '/calculators/mulch/',
  '/calculators/drywall/',
  '/calculators/flooring/',
  '/calculators/wallpaper/',
  '/calculators/gravel/',
  '/calculators/deck/',
  '/calculators/fence/',
  '/calculators/roofing/',
  '/calculators/insulation/',
  // content / info pages
  '/calculators/',
  '/my-sheets/',
  '/about/',
  '/how-to-use/',
  '/resources/',
  '/contact/',
  '/how-we-calculate/',
  '/terms/',
  '/privacy/',
  '/affiliate-disclosure/',
  // guides
  '/guides/how-to-paint-a-room/',
  '/guides/bathroom-renovation-checklist/',
  '/guides/kitchen-remodel-planning-guide/',
  '/guides/tile-installation-guide/',
  '/guides/deck-building-materials-list/',
  '/guides/concrete-mixing-and-pouring/',
])

export function localeFromPath(pathname: string): Locale {
  return pathname === '/fr' || pathname.startsWith('/fr/') ? 'fr' : 'en'
}

export function useLocale(): Locale {
  return localeFromPath(useLocation().pathname)
}

// Strip the /fr prefix → the canonical English path (with trailing slash, '/' for home).
export function toEnPath(pathname: string): string {
  let p = pathname
  if (p === '/fr' || p === '/fr/') return '/'
  if (p.startsWith('/fr/')) p = p.slice(3)
  if (p.length > 1 && !p.endsWith('/')) p += '/'
  return p
}

// Map an English canonical path to a locale-aware href. Falls back to English
// if the page has no French version yet.
export function localizedHref(enPath: string, locale: Locale): string {
  if (locale === 'fr' && FR_ENABLED.has(enPath)) {
    return enPath === '/' ? '/fr/' : '/fr' + enPath
  }
  return enPath
}
